// Deep-link -> modal bridge. app/middleware/modal.global.js rewrites paths
// like /login into ?modal=/login (preserving the rest of the query string,
// e.g. ?action=braindate) so the URL stays shareable/bookmarkable while the
// actual UI is an overlay-mounted modal rather than a route.
//
// `resolveComponent` (Vue's runtime helper) only resolves against the
// "currentRenderingInstance" — i.e. it must be called during a component's
// setup()/render(). A Nuxt plugin body runs at app-init time with no active
// component instance, so resolveComponent('FragmentsDialogsLogin') here would
// hit Vue's "can only be used in render() or setup()" warning and fall back
// to returning the raw string instead of the component definition. Importing
// the component directly sidesteps that entirely and is resolved statically
// at build time, so it's used below instead.
import LoginModal from '~/components/fragments/dialogs/login.vue';

export default defineNuxtPlugin(() => {
    const route = useRoute();
    const router = useRouter();
    const { open } = useModal();
    const { launchScheduleBuilder } = useScheduleBuilderLauncher();

    // Registry of deep-linkable modals, keyed by the path the middleware
    // stashes into `?modal=`. Extend this for search/reservations/etc. as
    // they migrate onto the overlay abstraction.
    const registry = { '/login': LoginModal };

    // Some deep links need more than mounting a component — the Schedule Builder
    // has to reset its wizard state, count the interaction, and re-hydrate the
    // session when the build finishes. Those get a launcher instead of a component,
    // which is the same function the agenda's own buttons call.
    const launchers = { '/build-schedule': launchScheduleBuilder };

    let active = null;

    const stripModalQuery = () => {
        const q = { ...route.query };
        delete q.modal;
        delete q.action;
        router.replace({ path: route.path, query: q, hash: route.hash });
    };

    watch(
        () => route.query.modal,
        async (modal) => {
            const key = typeof modal === 'string' ? modal.replace(/\/$/, '') : null;
            if (!key || active === key) return;
            if (!registry[key] && !launchers[key]) return;

            active = key;
            if (launchers[key]) {
                await launchers[key]();
            } else {
                // `action` (e.g. /login?modal=/login&action=braindate) is passed
                // through as an explicit prop rather than left for the modal to
                // re-derive from route.query, so there's one authoritative source
                // for "why was this modal opened" that survives the URL strip
                // below happening out from under it.
                const props = typeof route.query.action === 'string' ? { action: route.query.action } : {};
                await open(registry[key], props); // resolves when the modal closes
            }
            active = null;

            // Only strip if the query still points at the modal we opened —
            // if the user has already navigated elsewhere (or reopened a
            // different modal) while this one was closing, leave the URL alone.
            const cur = typeof route.query.modal === 'string' ? route.query.modal.replace(/\/$/, '') : null;
            if (cur === key) stripModalQuery();
        },
        { immediate: true }
    );
});
